import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { Outlet, useLocation, useNavigate } from 'react-router'
import { usePageConfig } from '@hooks/usePageConfig'
import ExternalHeaderContent from '@components/ExternalHeaderContent/ExternalHeaderContent'
import HeroBanner from '@components/HeroBanner/HeroBanner'
import Tabs from '@components/Tabs/Tabs'
import TutorialConnectorButton from '@components/TutorialConnectorButton/TutorialConnectorButton'
import CreateTokenButton from '@components/CreateTokenButton/CreateTokenButton'

import './Profile.css'

export default function Profile() {
  const intl = useIntl()
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const tabs = useMemo(
    () => [
      { id: 'profile', label: intl.formatMessage({ id: 'tab.profile' }) },
      { id: 'connectors', label: intl.formatMessage({ id: 'tab.connectors' }) },
      { id: 'tokens', label: intl.formatMessage({ id: 'tab.tokens' }) }
    ],
    [intl]
  )

  const activeTab = useMemo(() => {
    const currentTab = pathname.split('/')[2]
    return tabs.some((tab) => tab.id === currentTab) ? currentTab : 'profile'
  }, [pathname, tabs])

  const handleChange = useCallback(
    (id: string) => {
      navigate(id === 'profile' ? '/profile' : `/profile/${id}`)
    },
    [navigate]
  )

  const headerContent = useMemo(() => {
    const crumbs = [
      {
        label: intl.formatMessage({ id: 'nav.profile' }),
        path: '/profile'
      }
    ]

    const getHeaderActions = () => {
      switch (activeTab) {
        case 'connectors':
          return <TutorialConnectorButton />
        case 'tokens':
          return <CreateTokenButton />
        case 'profile':
        default:
          return null
      }
    }

    return (
      <ExternalHeaderContent
        hasBack
        crumbs={crumbs}
        headerActions={getHeaderActions()}
      />
    )
  }, [intl, activeTab])

  usePageConfig('profile', headerContent)

  return (
    <section id="profile-content" className="main-content">
      <HeroBanner nameId="profile" />
      <div className="container">
        <Tabs tabs={tabs} activeTab={activeTab} onChange={handleChange} />
        <Outlet />
      </div>
    </section>
  )
}
